import { Component, Input, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RegimenClass } from '@cureskin/api-client/src/server-api';
import { ConnectionService } from '@services/connection-service';
import { BroadcastService } from '@services/broadcast-service';
import { RegimenService } from '@services/regimen-service/regimen.service';
import { AppConfig } from '../../../../app.config';
import { RegimenExpiryService } from '@services/regimen-expiry-service/regimen-expiry.service';
import { ButtonModule } from '@components/button';
import { AllocatedRegimen } from '../../../../app-constant-types';
import { REGIMEN_ROUTE_PATHS, REGIMEN_QUERY_PARAMS } from '../regimen.constants';
import { RegimenEmptyStateComponent } from './regimen-empty-state/regimen-empty-state.component';

@Component({
  selector: 'user-view-regimen-empty',
  templateUrl: './user-view-regimen-empty.html',
  standalone: true,
  imports: [ButtonModule, RegimenEmptyStateComponent],
})
export class UserViewRegimenEmptyComponent implements OnInit {
  regimen: AllocatedRegimen;
  @Input('regimen')
  set setRegimen(regimen: AllocatedRegimen) {
    this.regimen = regimen;
    this.setConcerns();
    this.setImageUrl();
  }
  @Input() regimens: any[] = [];
  @Input('user') user: any;
  loading: boolean;
  concerns: string[] = [];
  iconSrc: string;
  showCrossBanner: boolean = true;
  isPaidUser: boolean = false;
  isInternalUser: boolean = false;

  // Dual-mode: route mode when rendered as a route child of the shell
  isRouteMode = false;
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly regimenService = inject(RegimenService);
  private readonly broadcast = inject(BroadcastService);

  constructor(
    public appConfig: AppConfig,
    private readonly conn: ConnectionService,
    private readonly regimenExpiryService: RegimenExpiryService,
  ) {}

  async ngOnInit(): Promise<void> {
    this.isRouteMode = this.activatedRoute.snapshot.routeConfig?.path === REGIMEN_ROUTE_PATHS.EMPTY;

    if (this.isRouteMode) {
      this.user = this.conn.getActingUser();
      const regimenClass = (this.activatedRoute.snapshot.queryParams[REGIMEN_QUERY_PARAMS.CLASS] as RegimenClass)
        || this.appConfig.Shared.Regimen.Class.FACE;
      const regimens = await this.regimenService.getRegimens();
      this.regimens = regimens;
      const regimen = regimens.find(r => r.class === regimenClass) || { class: regimenClass };
      this.setRegimen = regimen as AllocatedRegimen;
    }

    this.isPaidUser = this.user?.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED;
    this.isInternalUser = this.conn.isInternalUser();
    this.setImageUrl();
  }

  setImageUrl(): void {
    const regimenClass: string = this.regimen.class.toString();
    const isBodyRegimen = regimenClass === this.appConfig.Shared.Regimen.Class.BODY;
    this.user = this.conn.getActingUser();
    if (this.user?.get('Gender') === 'Female') {
      this.iconSrc = `https://cdn.cureskin.com/app/img/female-${regimenClass.toLowerCase()}.png`;
    } else {
      const lowerCaseRegimen = this.capitalizeWord(regimenClass.toLowerCase());
      this.iconSrc = `https://cdn.cureskin.com/app/img/Male%20${lowerCaseRegimen}_1.png`;
    }
    if (!isBodyRegimen) {
      if (this.user?.get('Gender') === 'Female') {
        this.iconSrc = `https://cdn.cureskin.com/app/img/female-${regimenClass.toLowerCase()}-new.png`;
      } else {
        this.iconSrc = `https://cdn.cureskin.com/app/img/male-${regimenClass.toLowerCase()}-new.png`;
      }
    }
    if (this.user?.get('Gender') === this.appConfig.Shared.Gender.MALE) {
      this.iconSrc = regimenClass === this.appConfig.Shared.Regimen.Class.HAIR ? 'https://cdn.cureskin.com/app/img/rebranded-male-hair.svg' : 'https://cdn.cureskin.com/app/img/rebranded-male-face.svg';
    } else {
      this.iconSrc = regimenClass === this.appConfig.Shared.Regimen.Class.HAIR ? 'https://cdn.cureskin.com/app/img/rebranded-female-hair.svg' : 'https://cdn.cureskin.com/app/img/rebranded-female-face.svg';
    }
  }

  capitalizeWord(word: string): string {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }

  setConcerns(): any {
    switch (this.regimen.class) {
      case this.appConfig.Shared.Regimen.Class.FACE: {
        this.concerns = [
          this.appConfig.Shared.String.DARKSPOTS,
          this.appConfig.Shared.String.ACNE_OR_PIMPLE,
          this.appConfig.Shared.String.DARK_CIRCLES,
          this.appConfig.Shared.String.DULL_SKIN,
          this.appConfig.Shared.String.MELASMA,
        ];
        break;
      }
      case this.appConfig.Shared.Regimen.Class.BODY: {
        this.concerns = [
          this.appConfig.Shared.String.STRETCH_MARKS,
          this.appConfig.Shared.String.RASH,
          this.appConfig.Shared.String.FUNGAL_INFECTION,
          this.appConfig.Shared.String.TINEA,
          this.appConfig.Shared.String.DRY_SKIN,
        ];
        break;
      }
      case this.appConfig.Shared.Regimen.Class.HAIR: {
        this.concerns = [
          this.appConfig.Shared.String.HAIRFALL,
          this.appConfig.Shared.String.DANDRUFF,
          this.appConfig.Shared.String.ITCHY_SCALP,
          this.appConfig.Shared.String.DRY_HAIR,
          this.appConfig.Shared.String.SCALP_PSORIASIS,
        ];
        break;
      }
      default:
        break;
    }
  }

  get isRegimenExpiredForPaidUser(): boolean {
    return this.regimenExpiryService.isRegimenExpiredForPaidUser(this.isPaidUser, this.regimens);
  }

  get isRegimenExpired() {
    return this.regimenExpiryService.shouldShowRegimenExpiredBanner({
      regimens: this.regimens,
      totalAmountSpent: this.user.get('totalAmountSpent'),
      isPaidUser: this.isPaidUser,
      consultationSession: undefined,
      skipHide: true
    });
  }

  async triggerTreeForRegimen(): Promise<void> {
    const regimenClass = this.regimen?.class as RegimenClass;
    const session = await this.conn.findConsultationSession(regimenClass);
    if (session && session?.get('PrivateMainConcern')) {
      try {
        const concerns = (session?.get('concernList') || []).length ? session?.get('concernList') : [session?.get('PrivateMainConcern')];
        await this.conn.saveUserMainConcern(concerns);
        await this.router.navigate([REGIMEN_ROUTE_PATHS.USER],
          { queryParams: { [REGIMEN_QUERY_PARAMS.TAB]: 'chat', username: this.user?.get('username') } });
      } catch (err) {
        this.broadcast.broadcast('NOTIFY', { message: err.toString() });
      }
    } else {
      await this.router.navigate([REGIMEN_ROUTE_PATHS.MAIN_CONCERN], {
        queryParams: { [REGIMEN_QUERY_PARAMS.CLASS]: regimenClass, [REGIMEN_QUERY_PARAMS.REDIRECT_ON_BACK]: '/home' },
      });
    }
  }
}
